import { filter, map, Observable } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { Site, ContainerWasteTypesGQL, AllWasteTypesGQL, Maybe } from '@iss/libs/graphql';
import { FormControl, NgControl, ReactiveFormsModule } from '@angular/forms';
import {
  Component,
  Inject,
  inject,
  Input,
  OnChanges,
  OnInit,
  Self,
  SimpleChanges,
} from '@angular/core';

import { WasteType, WasteTypes } from './waste-type-input.types';

import { WasteTypePipe } from '@iss/wo-common/pipes';
import { WasteTypeComponent } from '@iss/wo-common/components';
import { TypeaheadSelectComponent, TypeaheadSelectItemDirective } from '@iss/core/typeahead-select';
import { Control, NOOP_VALUE_ACCESSOR } from '@iss/core/forms';

@Component({
  selector: 'wo-waste-type-input',
  templateUrl: './waste-type-input.component.html',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    TypeaheadSelectComponent,
    TypeaheadSelectItemDirective,
    WasteTypeComponent,
  ],
  providers: [
    WasteTypePipe,
  ],
})
export class WasteTypeInputComponent implements OnInit, OnChanges {
  private readonly containerWasteTypesGQL = inject(ContainerWasteTypesGQL);
  private readonly allWasteTypesGQL = inject(AllWasteTypesGQL);
  private readonly wasteTypePipe = inject(WasteTypePipe);

  public loading$!: Observable<boolean>;
  public items$!: Observable<WasteTypes>;

  public readonly matcher = (wasteType: WasteType): string => this.wasteTypePipe.transform(wasteType);

  public readonly content = (code: WasteType['wasteTypeCode'], wasteTypes: WasteTypes | null): WasteType['presentedName'] | undefined => {
    const waste = wasteTypes?.find(wt => wt.wasteTypeCode === code);
    return this.wasteTypePipe.transform(waste);
  };

  public readonly stringify = (wasteTypes: WasteTypes | null) => (wasteType: WasteType['wasteTypeCode']) => {
    const waste = wasteTypes?.find(({ wasteTypeCode }) => wasteTypeCode === wasteType);
    return waste ? this.matcher(waste) : '';
  };

  public identity = (wasteType: WasteType): WasteType['wasteTypeCode'] => wasteType.wasteTypeCode;

  public constructor(
    @Self() @Inject(NgControl) public readonly control: Control<FormControl<WasteType['wasteTypeCode'] | null>>,
  ) {
    this.control.valueAccessor = NOOP_VALUE_ACCESSOR;
  }

  @Input() public placeholder: Maybe<string>;
  @Input() public readonly: Maybe<boolean>;
  @Input() public textFieldCleaner: Maybe<boolean>;
  @Input() public available: Maybe<readonly WasteType['wasteTypeCode'][]>;
  @Input() public siteId: Maybe<Site['id']>;
  @Input() public loading: Maybe<boolean>;
  @Input() public multi: Maybe<boolean>;
  @Input() public clearable = true;

  public ngOnInit(): void {
    this.initQuery();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ('available' in changes || 'siteId' in changes) {
      this.initQuery();
    }
  }

  private initQuery(): void {
    if (this.siteId) {
      const query$ = this.containerWasteTypesGQL.watch({ input: { siteId: this.siteId } }).valueChanges;

      this.loading$ = query$.pipe(map(({ loading }) => loading));
      this.items$ = query$.pipe(
        filter(({ data }) => Boolean(data)),
        map(({ data }) => data.containerWasteTypes),
        map(items => this.available ? items.filter(({ wasteTypeCode }) => this.available?.includes(wasteTypeCode)) : items),
      );
    } else {
      const query$ = this.allWasteTypesGQL.watch({ includeInactive: false }).valueChanges;

      this.loading$ = query$.pipe(map(({ loading }) => loading));
      this.items$ = query$.pipe(
        filter(({ data }) => Boolean(data)),
        map(({ data }) => data.wasteTypes),
        map(items => this.available ? items.filter(({ wasteTypeCode }) => this.available?.includes(wasteTypeCode)) : items),
      );
    }
  }
}
